import React from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Fullscreen, FullscreenExit, ArrowForward, DragIndicator, TouchApp } from "@material-ui/icons";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableList } from "shared/SortableList";
import { SortableRow } from "./editorDnd";
import { useHistory } from "react-router-dom";
import {
  useQuestionnaire,
  useQuestionnaires,
  useQuestions,
  useQuestionGroups,
  useUpdateQuestionnaire,
  useDeleteQuestionnaire,
  useCreateQuestionnaire,
} from "../../queries";
import { isPredicateValidationError } from "../../api";
import { LocalizedText, primaryText, emptyLocalizedText } from "../../types/common";
import { Questionnaire, Part, Page, expandedQuestionnaireToPersisted } from "../../types/persisted";
import TranslatedTextField from "../TranslatedTextField";
import {
  reorderArray,
  getResponseOptionTexts,
  scrollAndFlash,
  isValidInternalName,
  normalizeInternalName,
  structurallyEqual,
  errorMessage,
  INTERNAL_NAME_HINT,
} from "./editorUtils";
import { cx } from "@emotion/css";
import { editorStyles as styles } from "./editorStyles";
import { PredicateField } from "./feelResolver";
import AiChatPanel from "./AiChatPanel";

import type { PickType, PickedItem } from "../../uiTypes";

const QuestionnaireEditor: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  currentPageIndex?: number;
  onPageIndexChange?: (index: number) => void;
  onRequestPick: (pickType: PickType) => void;
  pickedItem: PickedItem;
  onPickConsumed: () => void;
  isPicking: boolean;
  highlight?: string | null;
  onHighlightConsumed?: () => void;
  onDeleted?: () => void;
  onUnsavedChange?: (id: number, dirty: boolean) => void;
}> = ({
  id,
  isExpanded,
  onToggleExpand,
  currentPageIndex = 0,
  onPageIndexChange,
  onRequestPick,
  pickedItem,
  onPickConsumed,
  isPicking,
  highlight,
  onHighlightConsumed,
  onDeleted,
  onUnsavedChange,
}) => {
  const history = useHistory();
  const { data: questionnaire, isLoading, error } = useQuestionnaire(id);
  const questionnairesQuery = useQuestionnaires();
  const hasUnpublishedChanges = !!questionnairesQuery.data?.find(q => q.id === id)?.has_unpublished_changes;
  const questionsQuery = useQuestions();
  const questionGroupsQuery = useQuestionGroups();
  const updateQuestionnaire = useUpdateQuestionnaire();
  const deleteQuestionnaire = useDeleteQuestionnaire();
  const createQuestionnaire = useCreateQuestionnaire();
  const [editedQuestionnaire, setEditedQuestionnaire] = React.useState<Questionnaire | null>(null);
  const [expandedVisibility, setExpandedVisibility] = React.useState<string | null>(null);
  const [expandedRequirement, setExpandedRequirement] = React.useState<string | null>(null);
  const [editorMode, setEditorMode] = React.useState<"structured" | "ai">("structured");
  const [aiInput, setAiInput] = React.useState("");
  const [aiTarget, setAiTarget] = React.useState<{
    pageIdx: number;
    partIdx: number;
    kind: "visibility" | "requirement";
    questionText: string;
    questionId: number;
    entityType: "question" | "question_group";
  } | null>(null);
  const [editingPredicate, setEditingPredicate] = React.useState<string | null>(null);
  const [pickTarget, setPickTargetState] = React.useState<{ pageIdx: number; partIdx: number } | null>(null);
  const pickTargetRef = React.useRef<{ pageIdx: number; partIdx: number } | null>(null);
  const setPickTarget = React.useCallback((target: { pageIdx: number; partIdx: number } | null) => {
    pickTargetRef.current = target;
    setPickTargetState(target);
  }, []);
  const [scrollToLast, setScrollToLast] = React.useState(0);
  const lastPartRef = React.useRef<HTMLDivElement>(null);
  const savedQuestionnaire = React.useRef<Questionnaire | null>(null);
  const editedQuestionnaireRef = React.useRef<Questionnaire | null>(null);
  editedQuestionnaireRef.current = editedQuestionnaire;
  const partKeyCounter = React.useRef(0);
  const partKeysRef = React.useRef<string[][]>([]);

  const partRowKey = (pageIdx: number, partIdx: number): string =>
    partKeysRef.current[pageIdx]?.[partIdx] ?? `part-${pageIdx}-${partIdx}`;

  const feelIdToText = React.useMemo(() => {
    if (!questionnaire) return {};
    const map: Record<string, string> = {};
    for (const page of questionnaire.pages) {
      for (const part of page.parts) {
        if (part._type === "question" && part.question?.feel_id && part.question.question) {
          map[part.question.feel_id] = primaryText(part.question.question);
        }
        if (part._type === "question_group" && part.question_group) {
          for (const qRef of part.question_group.questions) {
            if (qRef.question?.feel_id && qRef.question.question) {
              map[qRef.question.feel_id] = primaryText(qRef.question.question);
            }
          }
        }
      }
    }
    return map;
  }, [questionnaire]);

  const optionFeelIdToText = React.useMemo(() => {
    if (!questionnaire) return {};
    const map: Record<string, string> = {};
    for (const page of questionnaire.pages) {
      for (const part of page.parts) {
        if (part._type === "question" && part.question?.response_type) {
          Object.assign(map, getResponseOptionTexts(part.question.response_type));
        }
        if (part._type === "question_group" && part.question_group) {
          for (const qRef of part.question_group.questions) {
            if (qRef.question?.response_type) {
              Object.assign(map, getResponseOptionTexts(qRef.question.response_type));
            }
          }
        }
      }
    }
    return map;
  }, [questionnaire]);

  const predicateErrors = React.useMemo(() => {
    const err = updateQuestionnaire.error;
    if (!isPredicateValidationError(err)) return {};
    const map: Record<string, string> = {};
    for (const d of err.details) {
      map[d.field] = d.message;
    }
    return map;
  }, [updateQuestionnaire.error]);

  React.useEffect(() => {
    if (!questionnaire) return;
    const current = editedQuestionnaireRef.current;
    const saved = savedQuestionnaire.current;
    if (current && saved && !structurallyEqual(current, saved)) return;
    const fromServer = expandedQuestionnaireToPersisted(questionnaire);
    setEditedQuestionnaire(fromServer);
    savedQuestionnaire.current = fromServer;
    partKeysRef.current = fromServer.pages.map(page => page.parts.map(() => `pk-${partKeyCounter.current++}`));
  }, [questionnaire]);

  React.useEffect(() => {
    const err = updateQuestionnaire.error;
    if (!isPredicateValidationError(err) || !err.details.length) return;
    const first = err.details[0];
    const pageMatch = first.field.match(/^pages\[(\d+)\]\.parts\[(\d+)\]\.(visibility|requirement)$/);
    if (!pageMatch) return;
    const [, pageStr, partStr, kind] = pageMatch;
    const pageIdx = Number(pageStr);
    const partIdx = Number(partStr);
    onPageIndexChange?.(pageIdx);
    const key = partRowKey(pageIdx, partIdx);
    if (kind === "visibility") setExpandedVisibility(key);
    else setExpandedRequirement(key);
  }, [updateQuestionnaire.error, onPageIndexChange]);

  const hasUnsavedChanges = !!(
    editedQuestionnaire &&
    savedQuestionnaire.current &&
    !structurallyEqual(editedQuestionnaire, savedQuestionnaire.current)
  );

  React.useEffect(() => {
    onUnsavedChange?.(id, hasUnsavedChanges);
  }, [id, hasUnsavedChanges, onUnsavedChange]);
  React.useEffect(() => () => onUnsavedChange?.(id, false), [id, onUnsavedChange]);

  const hasUnresolvedRefs = !!editedQuestionnaire?.pages.some(page =>
    page.parts.some(
      part =>
        (part._type === "question" && part.question_id === 0) ||
        (part._type === "question_group" && part.question_group_id === 0)
    )
  );

  const updatePartQuestion = React.useCallback((pageIdx: number, partIdx: number, questionId: number) => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question") {
        newParts[partIdx] = { ...part, question_id: questionId };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  }, []);

  const updatePartQuestionGroup = React.useCallback((pageIdx: number, partIdx: number, questionGroupId: number) => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question_group") {
        newParts[partIdx] = { ...part, question_group_id: questionGroupId };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  }, []);

  React.useEffect(() => {
    const target = pickTargetRef.current;
    if (!pickedItem || !target) return;
    if (pickedItem.pickType === "question") {
      updatePartQuestion(target.pageIdx, target.partIdx, pickedItem.id);
    } else if (pickedItem.pickType === "questionGroup") {
      updatePartQuestionGroup(target.pageIdx, target.partIdx, pickedItem.id);
    }
    setPickTarget(null);
    onPickConsumed();
  }, [pickedItem, updatePartQuestion, updatePartQuestionGroup, setPickTarget, onPickConsumed]);

  React.useEffect(() => {
    if (!isPicking) setPickTarget(null);
  }, [isPicking, setPickTarget]);

  React.useEffect(() => {
    if (scrollToLast > 0 && lastPartRef.current) {
      lastPartRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [scrollToLast]);

  React.useEffect(() => {
    if (!highlight || !editedQuestionnaire) return;
    const [type, idStr] = highlight.split(":");
    const targetId = parseInt(idStr, 10);
    if (!type || isNaN(targetId)) return;

    const domId = type === "q" ? `part-q-${targetId}` : `part-qg-${targetId}`;
    scrollAndFlash(domId, onHighlightConsumed);
  }, [highlight, editedQuestionnaire, onHighlightConsumed]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography style={{ color: "#f44336" }}>Failed to load questionnaire</Typography>;
  if (!editedQuestionnaire) return null;

  const handleSave = () => {
    const name = editedQuestionnaire.name ?? "";
    if (name && !isValidInternalName(name)) {
      const normalized = normalizeInternalName(name);
      if (!window.confirm(`"${name}" is not a valid internal name. Save as "${normalized}"?`)) return;
      const snapshot = { ...editedQuestionnaire, name: normalized };
      setEditedQuestionnaire(prev => (prev ? { ...prev, name: normalized } : prev));
      updateQuestionnaire.mutate(
        { id, questionnaire: snapshot },
        {
          onSuccess: () => {
            savedQuestionnaire.current = snapshot;
          },
        }
      );
      return;
    }
    const snapshot = editedQuestionnaire;
    updateQuestionnaire.mutate(
      { id, questionnaire: snapshot },
      {
        onSuccess: () => {
          savedQuestionnaire.current = snapshot;
        },
      }
    );
  };

  const addPage = () => {
    partKeysRef.current.push([]);
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPage: Page = { title: emptyLocalizedText(), parts: [] };
      onPageIndexChange?.(prev.pages.length);
      return { ...prev, pages: [...prev.pages, newPage] };
    });
  };

  const removePage = (pageIdx: number) => {
    partKeysRef.current = partKeysRef.current.filter((_, i) => i !== pageIdx);
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = prev.pages.filter((_, i) => i !== pageIdx);
      if (pageIdx >= newPages.length) {
        onPageIndexChange?.(Math.max(0, newPages.length - 1));
      } else if (pageIdx < currentPageIndex) {
        onPageIndexChange?.(currentPageIndex - 1);
      }
      return { ...prev, pages: newPages };
    });
  };

  const updatePageTitle = (pageIdx: number, title: LocalizedText | undefined) => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      newPages[pageIdx] = { ...newPages[pageIdx], title };
      return { ...prev, pages: newPages };
    });
  };

  const removePartFromPage = (pageIdx: number, partIdx: number) => {
    if (partKeysRef.current[pageIdx]) {
      partKeysRef.current[pageIdx] = partKeysRef.current[pageIdx].filter((_, i) => i !== partIdx);
    }
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      newPages[pageIdx] = {
        ...newPages[pageIdx],
        parts: newPages[pageIdx].parts.filter((_, i) => i !== partIdx),
      };
      return { ...prev, pages: newPages };
    });
  };

  const onDragEndParts = (pageIdx: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const keys = partKeysRef.current[pageIdx];
    if (!keys) return;
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    partKeysRef.current[pageIdx] = reorderArray(keys, from, to);
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      newPages[pageIdx] = { ...newPages[pageIdx], parts: reorderArray(newPages[pageIdx].parts, from, to) };
      return { ...prev, pages: newPages };
    });
  };

  const setPartVisibility = (pageIdx: number, partIdx: number, type: "visible" | "visibleWhen") => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      newParts[partIdx] = {
        ...newParts[partIdx],
        visibility: type === "visible" ? "visible" : { visibleWhen: "" },
      };
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartVisibilityPredicate = (pageIdx: number, partIdx: number, predicate: string) => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      newParts[partIdx] = { ...newParts[partIdx], visibility: { visibleWhen: predicate } };
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartRequirement = (pageIdx: number, partIdx: number, type: "optional" | "required" | "requiredWhen") => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question") {
        newParts[partIdx] = {
          ...part,
          requirement: type === "requiredWhen" ? { requiredWhen: "" } : type,
        };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const setPartRequirementPredicate = (pageIdx: number, partIdx: number, predicate: string) => {
    setEditedQuestionnaire(prev => {
      if (!prev) return prev;
      const newPages = [...prev.pages];
      const newParts = [...newPages[pageIdx].parts];
      const part = newParts[partIdx];
      if (part._type === "question") {
        newParts[partIdx] = { ...part, requirement: { requiredWhen: predicate } };
      }
      newPages[pageIdx] = { ...newPages[pageIdx], parts: newParts };
      return { ...prev, pages: newPages };
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this questionnaire?`)) {
      deleteQuestionnaire.mutate(id, {
        onSuccess: () => onDeleted?.(),
      });
    }
  };

  const handleDuplicate = () => {
    if (!questionnaire) return;
    const persisted = expandedQuestionnaireToPersisted(questionnaire);
    const base = persisted.name && isValidInternalName(persisted.name) ? persisted.name : "untitled";
    const name = `${base}_copy_${Date.now()}`;
    createQuestionnaire.mutate(
      { name, pages: persisted.pages },
      {
        onSuccess: data => history.push(`/health-forms/questionnaires/${data.id}`),
      }
    );
  };

  return (
    <div className={styles.editorContainer} style={isPicking ? { pointerEvents: "none" } : undefined}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        style={isPicking ? { opacity: 0.3 } : undefined}
      >
        <Tabs
          value={editorMode}
          onChange={(_, value) => setEditorMode(value)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Structured Editor" value="structured" />
          <Tab label="AI Editor" value="ai" />
        </Tabs>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
            <IconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {editorMode === "ai" ? (
        <AiChatPanel
          entityLabel="questionnaire"
          aiTarget={aiTarget}
          input={aiInput}
          onInputChange={setAiInput}
          feelIdToText={feelIdToText}
          optionFeelIdToText={optionFeelIdToText}
          onApply={predicate => {
            if (!aiTarget) return;
            if (aiTarget.kind === "visibility") {
              setPartVisibilityPredicate(aiTarget.pageIdx, aiTarget.partIdx, predicate);
            } else {
              setPartRequirementPredicate(aiTarget.pageIdx, aiTarget.partIdx, predicate);
            }
            setEditorMode("structured");
          }}
        />
      ) : (
        <div className={cx(styles.form, isPicking && styles.formPicking)}>
          <Box
            className={styles.toolbar}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            style={{ gap: 8 }}
          >
            <Button size="small" onClick={addPage}>
              Add Page
            </Button>
            <Button
              size="small"
              style={{ color: "#f44336" }}
              onClick={handleDelete}
              disabled={deleteQuestionnaire.isLoading}
            >
              {deleteQuestionnaire.isLoading ? "Deleting..." : "Delete"}
            </Button>
            <Button size="small" onClick={handleDuplicate} disabled={createQuestionnaire.isLoading}>
              {createQuestionnaire.isLoading ? "Duplicating..." : "Duplicate"}
            </Button>
            {hasUnsavedChanges && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => {
                  if (savedQuestionnaire.current) {
                    setEditedQuestionnaire(savedQuestionnaire.current);
                    partKeysRef.current = savedQuestionnaire.current.pages.map(page =>
                      page.parts.map(() => `pk-${partKeyCounter.current++}`)
                    );
                  }
                }}
              >
                Revert
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={updateQuestionnaire.isLoading || !editedQuestionnaire.name?.trim()}
            >
              {updateQuestionnaire.isLoading ? "Saving..." : hasUnsavedChanges ? "Save*" : "Save"}
            </Button>
            {hasUnpublishedChanges && (
              <Tooltip
                title={
                  hasUnresolvedRefs
                    ? "Resolve all empty question/group parts before publishing"
                    : hasUnsavedChanges
                      ? "Save before publishing"
                      : ""
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => history.push(`/health-forms/review/${id}`)}
                    disabled={hasUnresolvedRefs || hasUnsavedChanges}
                  >
                    Review
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          <TextField
            label="Questionnaire Name"
            value={editedQuestionnaire.name ?? ""}
            onChange={e => setEditedQuestionnaire(prev => (prev ? { ...prev, name: e.target.value } : prev))}
            fullWidth
            variant="outlined"
            margin="normal"
            error={!isValidInternalName(editedQuestionnaire.name)}
            helperText={!isValidInternalName(editedQuestionnaire.name) ? INTERNAL_NAME_HINT : undefined}
          />

          {editedQuestionnaire.pages.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                No pages
              </Typography>
              <Button size="small" onClick={addPage} style={{ marginTop: 8 }}>
                Add Page
              </Button>
            </Box>
          ) : (
            (() => {
              const pageIdx = currentPageIndex;
              const page = editedQuestionnaire.pages[pageIdx];
              if (!page) return null;
              return (
                <>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }} mt={2} mb={1}>
                    <div className={styles.editorPageNav}>
                      {editedQuestionnaire.pages.map((p, idx) => (
                        <button
                          key={idx}
                          className={`${styles.editorPageNavButton} ${idx === pageIdx ? styles.editorPageNavButtonActive : ""}`}
                          onClick={() => onPageIndexChange?.(idx)}
                          title={p.title ? primaryText(p.title) || `Page ${idx + 1}` : `Page ${idx + 1}`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                    <Button size="small" style={{ color: "#f44336" }} onClick={() => removePage(pageIdx)}>
                      ✕
                    </Button>
                  </Box>

                  <Box mb={2}>
                    <TranslatedTextField
                      label="Page Title"
                      value={page.title ?? emptyLocalizedText()}
                      onChange={value => updatePageTitle(pageIdx, primaryText(value) ? value : undefined)}
                    />
                  </Box>

                  <Box display="flex" style={{ gap: 8 }} mb={2}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (!partKeysRef.current[pageIdx]) partKeysRef.current[pageIdx] = [];
                        partKeysRef.current[pageIdx].push(`pk-${partKeyCounter.current++}`);
                        setEditedQuestionnaire(prev => {
                          if (!prev) return prev;
                          const newPages = [...prev.pages];
                          const idx = pageIdx;
                          const newPart: Part = {
                            _type: "question",
                            question_id: 0,
                            visibility: "visible",
                            requirement: "optional",
                          };
                          newPages[idx] = { ...newPages[idx], parts: [...newPages[idx].parts, newPart] };
                          return { ...prev, pages: newPages };
                        });
                        setScrollToLast(c => c + 1);
                      }}
                    >
                      Add Question
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (!partKeysRef.current[pageIdx]) partKeysRef.current[pageIdx] = [];
                        partKeysRef.current[pageIdx].push(`pk-${partKeyCounter.current++}`);
                        setEditedQuestionnaire(prev => {
                          if (!prev) return prev;
                          const newPages = [...prev.pages];
                          const idx = pageIdx;
                          const newPart: Part = {
                            _type: "question_group",
                            question_group_id: 0,
                            visibility: "visible",
                          };
                          newPages[idx] = { ...newPages[idx], parts: [...newPages[idx].parts, newPart] };
                          return { ...prev, pages: newPages };
                        });
                        setScrollToLast(c => c + 1);
                      }}
                    >
                      Add Question Group
                    </Button>
                  </Box>

                  {page.parts.length === 0 && (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                      No parts yet
                    </Typography>
                  )}

                  <SortableList items={partKeysRef.current[pageIdx] ?? []} onDragEnd={onDragEndParts(pageIdx)}>
                    {page.parts.map((part, partIdx) => {
                      const isQuestion = part._type === "question";
                      const isPickTarget = pickTarget?.pageIdx === pageIdx && pickTarget?.partIdx === partIdx;
                      const bgColor = isPickTarget ? "#fff3cd" : isQuestion ? "#d1ecf1" : "#e2d9f3";
                      const isVisibilityConditional = typeof part.visibility === "object";
                      const isRequirementConditional = isQuestion && typeof part.requirement === "object";
                      const rowKey = partRowKey(pageIdx, partIdx);

                      return (
                        <SortableRow
                          key={rowKey}
                          id={rowKey}
                          domId={isQuestion ? `part-q-${part.question_id}` : `part-qg-${part.question_group_id}`}
                          extraStyle={{ marginBottom: 8 }}
                        >
                          {handleProps => (
                            <>
                              <Box
                                display="flex"
                                alignItems="center"
                                style={{
                                  gap: 8,
                                  padding: 8,
                                  background: bgColor,
                                  borderRadius: 4,
                                  border: isPickTarget ? "2px solid #ffc107" : undefined,
                                }}
                              >
                                <span {...handleProps} style={{ cursor: "grab", display: "flex" }}>
                                  <DragIndicator fontSize="small" style={{ color: "#888" }} />
                                </span>

                                {isQuestion ? (
                                  <Box display="flex" flexDirection="column" style={{ flex: 1, gap: 4, minWidth: 0 }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8, width: "100%" }}>
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        style={{ flex: 1, minWidth: 0, color: part.question_id ? undefined : "#999" }}
                                      >
                                        {(() => {
                                          const q = questionsQuery.data?.find(q => q.id === part.question_id);
                                          return q ? q.question || `Question #${q.id}` : "-- Select a question --";
                                        })()}
                                      </Typography>
                                      <Tooltip title="Pick question">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setPickTarget({ pageIdx, partIdx });
                                            onRequestPick("question");
                                          }}
                                          style={{ padding: 4, flexShrink: 0 }}
                                        >
                                          <TouchApp fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {!!part.question_id && (
                                        <Tooltip title="Open question">
                                          <IconButton
                                            size="small"
                                            onClick={() => history.push(`/health-forms/questions/${part.question_id}`)}
                                            style={{ padding: 4, flexShrink: 0 }}
                                          >
                                            <ArrowForward fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    {isPickTarget && (
                                      <Typography variant="caption" style={{ color: "#856404" }}>
                                        ← Select from sidebar
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Box display="flex" flexDirection="column" style={{ flex: 1, gap: 4, minWidth: 0 }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8, width: "100%" }}>
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        style={{
                                          flex: 1,
                                          minWidth: 0,
                                          color: part.question_group_id ? undefined : "#999",
                                        }}
                                      >
                                        {(() => {
                                          const qg = questionGroupsQuery.data?.find(
                                            qg => qg.id === part.question_group_id
                                          );
                                          return qg ? qg.name : "-- Select a question group --";
                                        })()}
                                      </Typography>
                                      <Tooltip title="Pick question group">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setPickTarget({ pageIdx, partIdx });
                                            onRequestPick("questionGroup");
                                          }}
                                          style={{ padding: 4, flexShrink: 0 }}
                                        >
                                          <TouchApp fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {!!part.question_group_id && (
                                        <Tooltip title="Open question group">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              history.push(`/health-forms/question-groups/${part.question_group_id}`)
                                            }
                                            style={{ padding: 4, flexShrink: 0 }}
                                          >
                                            <ArrowForward fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    {isPickTarget && (
                                      <Typography variant="caption" style={{ color: "#856404" }}>
                                        ← Select from sidebar
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                <Button
                                  size="small"
                                  onClick={() => setExpandedVisibility(expandedVisibility === rowKey ? null : rowKey)}
                                  style={{
                                    background: isVisibilityConditional ? "#ffc107" : "#28a745",
                                    color: isVisibilityConditional ? "#000" : "#fff",
                                  }}
                                >
                                  👁️
                                </Button>

                                {isQuestion && (
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      setExpandedRequirement(expandedRequirement === rowKey ? null : rowKey)
                                    }
                                    style={{
                                      background: isRequirementConditional
                                        ? "#ffc107"
                                        : part.requirement === "required"
                                          ? "#1976d2"
                                          : "#6c757d",
                                      color: isRequirementConditional ? "#000" : "#fff",
                                    }}
                                  >
                                    ⚙️
                                  </Button>
                                )}

                                <Button
                                  size="small"
                                  style={{ color: "#f44336" }}
                                  onClick={() => removePartFromPage(pageIdx, partIdx)}
                                >
                                  ✕
                                </Button>
                              </Box>

                              {expandedVisibility === rowKey && (
                                <Box className={styles.expandedSection}>
                                  <Typography variant="caption" gutterBottom display="block">
                                    Visibility
                                  </Typography>
                                  <Box display="flex" style={{ gap: 8 }} mb={1}>
                                    <Button
                                      size="small"
                                      variant={part.visibility === "visible" ? "contained" : "outlined"}
                                      onClick={() => setPartVisibility(pageIdx, partIdx, "visible")}
                                    >
                                      Visible
                                    </Button>
                                    <Button
                                      size="small"
                                      variant={isVisibilityConditional ? "contained" : "outlined"}
                                      onClick={() => setPartVisibility(pageIdx, partIdx, "visibleWhen")}
                                    >
                                      Visible When
                                    </Button>
                                  </Box>
                                  {typeof part.visibility === "object" && (
                                    <PredicateField
                                      predicate={part.visibility.visibleWhen}
                                      feelIdToText={feelIdToText}
                                      optionFeelIdToText={optionFeelIdToText}
                                      isEditing={editingPredicate === `visibility-${rowKey}`}
                                      onStartEditing={() => setEditingPredicate(`visibility-${rowKey}`)}
                                      onStopEditing={() => setEditingPredicate(null)}
                                      onChange={v => setPartVisibilityPredicate(pageIdx, partIdx, v)}
                                      error={predicateErrors[`pages[${pageIdx}].parts[${partIdx}].visibility`]}
                                      onAiRequest={() => {
                                        const name = isQuestion
                                          ? questionsQuery.data?.find(q => q.id === part.question_id)?.question
                                          : questionGroupsQuery.data?.find(qg => qg.id === part.question_group_id)
                                              ?.name;
                                        const id = isQuestion ? part.question_id : part.question_group_id;
                                        setAiInput(
                                          `Generate a visibility predicate for "${name ?? "unknown"}" (${isQuestion ? "question" : "question_group"} #${id})`
                                        );
                                        setAiTarget({
                                          pageIdx,
                                          partIdx,
                                          kind: "visibility",
                                          questionText: name ?? "unknown",
                                          questionId: id!,
                                          entityType: isQuestion ? "question" : "question_group",
                                        });
                                        setEditorMode("ai");
                                      }}
                                    />
                                  )}
                                </Box>
                              )}

                              {expandedRequirement === rowKey && isQuestion && (
                                <Box className={styles.expandedSectionAlt}>
                                  <Typography variant="caption" gutterBottom display="block">
                                    Requirement
                                  </Typography>
                                  <Box display="flex" style={{ gap: 8 }} mb={1}>
                                    <Button
                                      size="small"
                                      variant={part.requirement === "optional" ? "contained" : "outlined"}
                                      onClick={() => setPartRequirement(pageIdx, partIdx, "optional")}
                                    >
                                      Optional
                                    </Button>
                                    <Button
                                      size="small"
                                      variant={part.requirement === "required" ? "contained" : "outlined"}
                                      onClick={() => setPartRequirement(pageIdx, partIdx, "required")}
                                    >
                                      Required
                                    </Button>
                                    <Button
                                      size="small"
                                      variant={isRequirementConditional ? "contained" : "outlined"}
                                      onClick={() => setPartRequirement(pageIdx, partIdx, "requiredWhen")}
                                    >
                                      Required When
                                    </Button>
                                  </Box>
                                  {isQuestion && typeof part.requirement === "object" && (
                                    <PredicateField
                                      predicate={part.requirement.requiredWhen}
                                      feelIdToText={feelIdToText}
                                      optionFeelIdToText={optionFeelIdToText}
                                      isEditing={editingPredicate === `requirement-${rowKey}`}
                                      onStartEditing={() => setEditingPredicate(`requirement-${rowKey}`)}
                                      onStopEditing={() => setEditingPredicate(null)}
                                      onChange={v => setPartRequirementPredicate(pageIdx, partIdx, v)}
                                      error={predicateErrors[`pages[${pageIdx}].parts[${partIdx}].requirement`]}
                                      onAiRequest={() => {
                                        const name = questionsQuery.data?.find(
                                          q => q.id === part.question_id
                                        )?.question;
                                        setAiInput(
                                          `Generate a requirement predicate for "${name ?? "unknown"}" (question #${part.question_id})`
                                        );
                                        setAiTarget({
                                          pageIdx,
                                          partIdx,
                                          kind: "requirement",
                                          questionText: name ?? "unknown",
                                          questionId: part.question_id!,
                                          entityType: "question",
                                        });
                                        setEditorMode("ai");
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                            </>
                          )}
                        </SortableRow>
                      );
                    })}
                    <div ref={lastPartRef} />
                  </SortableList>
                </>
              );
            })()
          )}

          <Box mt={2}>
            <Typography variant="caption" color="textSecondary" display="block">
              ID: {editedQuestionnaire.id}
            </Typography>
            {editedQuestionnaire.updated_at && (
              <Typography variant="caption" color="textSecondary" display="block">
                Updated: {new Date(editedQuestionnaire.updated_at).toLocaleString()}
              </Typography>
            )}
            {editedQuestionnaire.edited_by && (
              <Typography variant="caption" color="textSecondary" display="block">
                Edited by: {editedQuestionnaire.edited_by}
              </Typography>
            )}
          </Box>

          {updateQuestionnaire.isError && !isPredicateValidationError(updateQuestionnaire.error) && (
            <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
              Failed to save: {errorMessage(updateQuestionnaire.error)}
            </Typography>
          )}
          {updateQuestionnaire.isError && isPredicateValidationError(updateQuestionnaire.error) && (
            <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
              Failed to save: {updateQuestionnaire.error.details.length} predicate error(s) — see highlighted fields
              above
            </Typography>
          )}
          {updateQuestionnaire.isSuccess && (
            <Typography color="primary" variant="caption" display="block" style={{ marginTop: 8 }}>
              Saved successfully
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireEditor;
